// PageBoundary — the per-page error wall the portals never had (source
// DashboardLayout wrapped every page; a render throw previously blanked the
// whole app). Solid's ErrorBoundary, keyed by the current pathname so
// NAVIGATING AWAY from a crashed page automatically retries a fresh subtree,
// plus an explicit "Try again" reset for the same page.
import { ErrorBoundary, Show, createSignal, type JSX } from 'solid-js';
import { RotateCcw, TriangleAlert } from 'lucide-solid';

export function PageBoundary(props: {
  /** A key that changes when the user navigates (e.g. the pathname) — remounts the subtree. */
  resetKey?: string;
  children: JSX.Element;
}) {
  const [generation, setGeneration] = createSignal(0);
  return (
    // keyed on navigation + manual resets: any change remounts the boundary
    <Show when={`${props.resetKey ?? ''}·${generation()}`} keyed>
      <ErrorBoundary
        fallback={(err: unknown) => (
          // border stays: an alert frame is structurally load-bearing (Spectrum in-line alert), now opaque
          <div role="alert" class="mx-auto my-10 flex max-w-lg flex-col items-center gap-3 rounded-500 border border-error bg-error-tint p-8 text-center">
            <TriangleAlert size={28} class="text-error" aria-hidden="true" />
            {/* h1: the fallback REPLACES the page, so it carries the page's one
                heading (UX-13 — error pages had none; spectrum.css §2b inks it). */}
            <h1 class="text-lg">This page hit an error</h1>
            <p class="max-w-prose text-sm text-base-subtle">
              {err instanceof Error ? err.message : String(err)}
            </p>
            <div class="mt-1 flex items-center gap-2">
              <button type="button" class="btn btn-primary btn-sm gap-2" onClick={() => setGeneration((g) => g + 1)}>
                <RotateCcw size={14} aria-hidden="true" />
                Try again
              </button>
              <a href="/" class="btn btn-sm" data-variant="secondary">
                Back to home
              </a>
            </div>
          </div>
        )}
      >
        {props.children}
      </ErrorBoundary>
    </Show>
  );
}
